import React, { useRef, useState } from 'react';
import { getBindingRecordId, resolveBindingValue } from './TemplateBindings';
import { getCanvasMargins, getDataContentBounds, getRegionHeight, getRepeatCardLayout } from './TemplateLayout';

const TemplateCanvas = ({
  activeRegion,
  bindingData,
  dataContentConfig,
  previewRecords,
  regionElements,
  setActiveRegion,
  template,
  selectedElementId,
  setSelectedElementId,
  updateElement,
}) => {
  const canvasRef = useRef(null);
  const [interaction, setInteraction] = useState(null);
  const [zoom, setZoom] = useState(0.75);

  const getCanvasPoint = (event) => {
    const canvasBounds = canvasRef.current.getBoundingClientRect();

    return {
      x: (event.clientX - canvasBounds.left) / zoom,
      y: (event.clientY - canvasBounds.top) / zoom,
    };
  };

  const getRegionBounds = (regionName) => {
    const headerHeight = getRegionHeight(template, 'header');
    const footerHeight = getRegionHeight(template, 'footer');
    const margins = getCanvasMargins(template);
    const contentRight = template.canvas.width - margins.left - margins.right;

    if (regionName === 'header') {
      return { left: 0, right: contentRight, top: 0, bottom: headerHeight };
    }

    if (regionName === 'footer') {
      return { left: 0, right: contentRight, top: template.canvas.height - footerHeight, bottom: template.canvas.height };
    }

    if (dataContentConfig.mode === 'repeat') {
      const cardLayout = getRepeatCardLayout(template);

      return { left: 0, right: cardLayout.cardWidth, top: 0, bottom: cardLayout.cardHeight };
    }

    return {
      left: 0,
      right: contentRight,
      top: headerHeight,
      bottom: template.canvas.height - margins.top - margins.bottom - footerHeight,
    };
  };

  const handlePointerDown = (event, element, regionName, mode = 'move') => {
    event.preventDefault();
    event.stopPropagation();

    const point = getCanvasPoint(event);
    setActiveRegion(regionName);
    setSelectedElementId(element.id);
    setInteraction({
      mode,
      element,
      regionName,
      pointerId: event.pointerId,
      startX: point.x,
      startY: point.y,
    });
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handlePointerMove = (event) => {
    if (!interaction) {
      return;
    }

    const point = getCanvasPoint(event);
    const deltaX = point.x - interaction.startX;
    const deltaY = point.y - interaction.startY;
    const { element, mode, regionName } = interaction;
    const bounds = getRegionBounds(regionName);

    if (mode === 'move') {
      updateElement(element.id, {
        x: Math.max(bounds.left, Math.min(bounds.right - element.width, element.x + deltaX)),
        y: Math.max(bounds.top, Math.min(bounds.bottom - element.height, element.y + deltaY)),
      }, regionName);
      return;
    }

    updateElement(element.id, {
      width: Math.max(24, Math.min(bounds.right - element.x, element.width + deltaX)),
      height: Math.max(24, Math.min(bounds.bottom - element.y, element.height + deltaY)),
    }, regionName);
  };

  const handlePointerUp = (event) => {
    if (interaction && event.currentTarget.hasPointerCapture(interaction.pointerId)) {
      event.currentTarget.releasePointerCapture(interaction.pointerId);
    }

    setInteraction(null);
  };

  const handleCanvasPointerDown = (event) => {
    if (event.target === canvasRef.current) {
      setSelectedElementId(null);
    }
  };

  const handleRegionPointerDown = (event, regionName) => {
    event.stopPropagation();
    setActiveRegion(regionName);
    setSelectedElementId(null);
  };

  const renderElement = (element, regionName, options = {}) => {
    const isEditable = options.isEditable !== false;
    const isSelected = isEditable && activeRegion === regionName && selectedElementId === element.id;
    const isText = element.type === 'text';
    const isImage = element.type === 'image';
    const isLine = element.type === 'line';
    const isRectangle = element.type === 'rectangle';
    const isTable = element.type === 'table';
    const elementBindingData = options.bindingData || bindingData;
    const margins = getCanvasMargins(template);
    const pageOffset = options.hasPageOffset === false
      ? { x: 0, y: 0 }
      : { x: margins.left, y: regionName === 'footer' ? -margins.bottom : margins.top };
    const boundValue = resolveBindingValue(elementBindingData, element.fieldPath);
    const hasBoundValue = boundValue !== null && boundValue !== undefined && boundValue !== '';
    const textContent = hasBoundValue
      ? boundValue
      : element.fieldPath
        ? `{${element.displayName || element.fieldPath}}`
        : element.content || 'Text block';
    const elementStyle = isText
      ? {
          backgroundColor: element.styles?.backgroundColor || 'transparent',
          color: element.styles?.color || '#1f2937',
          fontFamily: element.styles?.fontFamily || 'Georgia, serif',
          fontSize: element.styles?.fontSize || 14,
          fontWeight: element.styles?.fontWeight || 'normal',
          lineHeight: element.styles?.lineHeight || 1.25,
          textAlign: element.styles?.textAlign || 'left',
        }
      : isLine
        ? {
            alignItems: 'center',
            backgroundColor: 'transparent',
            border: 'none',
            display: 'flex',
            justifyContent: 'center',
          }
      : {
          backgroundColor: isRectangle ? element.styles?.fill || 'transparent' : element.styles?.fill || '#e8f1fb',
          borderColor: element.styles?.stroke || '#2b6cb0',
          borderWidth: element.styles?.strokeWidth || 1,
          borderStyle: 'solid',
        };

    return (
      <div
        className={`pdf-template-designer__canvas-element is-${regionName} ${isSelected ? 'is-selected' : ''} ${isText ? 'is-text' : isImage ? 'is-image' : isTable ? 'is-table' : isLine ? 'is-line' : 'is-shape'}`}
        key={options.key || `${regionName}-${element.id}`}
        style={{
          ...elementStyle,
          left: (options.left ?? element.x) + pageOffset.x,
          top: (options.top ?? element.y) + pageOffset.y,
          width: element.width,
          height: element.height,
          pointerEvents: isEditable ? 'auto' : 'none',
        }}
        onPointerDown={(event) => {
          if (isEditable) {
            handlePointerDown(event, element, regionName);
          }
        }}
      >
        {isText ? textContent : null}
        {isLine ? (
          <span
            className="pdf-template-designer__canvas-line"
            style={{
              backgroundColor: element.styles?.stroke || '#2b6cb0',
              height: element.orientation === 'vertical' ? '100%' : element.styles?.strokeWidth || 1,
              width: element.orientation === 'vertical' ? element.styles?.strokeWidth || 1 : '100%',
            }}
          />
        ) : null}
        {isImage && element.imageUrl ? (
          <img
            alt={element.name || 'Template element'}
            className={`pdf-template-designer__canvas-image is-${element.imageMode || 'contain'}`}
            src={element.imageUrl}
          />
        ) : null}
        {isImage && !element.imageUrl ? (
          <span className="pdf-template-designer__canvas-image-placeholder">Image</span>
        ) : null}
        {isTable ? (
          <table className="pdf-template-designer__canvas-table">
            {element.showHeaders !== false ? (
              <thead>
                <tr>
                  {(element.columns || []).map((column) => {
                    return <th key={column.key}>{column.label || column.key}</th>;
                  })}
                </tr>
              </thead>
            ) : null}
            <tbody>
              {(Array.isArray(resolveBindingValue(elementBindingData, element.fieldPath))
                ? resolveBindingValue(elementBindingData, element.fieldPath)
                : element.tableData || []).map((row, rowIndex) => {
                return (
                  <tr key={rowIndex}>
                    {(element.columns || []).map((column) => {
                      const cellValue = column.fieldPath ? resolveBindingValue(row, column.fieldPath) : row[column.key];

                      return <td key={column.key}>{cellValue || ''}</td>;
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : null}
        {isSelected ? (
          <button
            aria-label="Resize selected element"
            className="pdf-template-designer__resize-handle"
            onPointerDown={(event) => {
              handlePointerDown(event, element, regionName, 'resize');
            }}
            type="button"
          />
        ) : null}
      </div>
    );
  };

  const renderDataContent = () => {
    if (dataContentConfig.mode !== 'repeat') {
      return regionElements.dataContent.map((element) => {
        return renderElement(element, 'dataContent');
      });
    }

    const cardLayout = getRepeatCardLayout(template);
    const cardCount = cardLayout.columns * cardLayout.rows;

    return Array.from({ length: cardCount }).map((item, cardIndex) => {
      const columnIndex = cardIndex % cardLayout.columns;
      const rowIndex = Math.floor(cardIndex / cardLayout.columns);
      const cardBindingData = previewRecords[cardIndex] || bindingData;
      const recordId = getBindingRecordId(dataContentConfig.dataSource, cardBindingData);
      const cardX = cardLayout.x + columnIndex * (cardLayout.cardWidth + cardLayout.gap);
      const cardY = cardLayout.y + rowIndex * (cardLayout.cardHeight + cardLayout.gap);

      return (
        <div
          className="pdf-template-designer__repeat-card"
          key={recordId || `card-${cardIndex}`}
          style={{
            height: cardLayout.cardHeight,
            left: cardX,
            pointerEvents: cardIndex === 0 ? 'auto' : 'none',
            top: cardY,
            width: cardLayout.cardWidth,
          }}
        >
          <span>Card {cardIndex + 1}</span>
          {regionElements.dataContent.map((element) => {
            return renderElement(element, 'dataContent', {
              bindingData: cardBindingData,
              isEditable: cardIndex === 0,
              key: `card-${cardIndex}-${element.id}`,
              hasPageOffset: false,
              left: element.x,
              top: element.y,
            });
          })}
        </div>
      );
    });
  };

  return (
    <div className="pdf-template-designer__canvas-panel">
      <div className="pdf-template-designer__canvas-toolbar">
        <div>
          <span>Canvas Preview</span>
          {activeRegion === 'dataContent' ? (
            <small className="pdf-template-designer__canvas-mode">
              {dataContentConfig.mode === 'repeat'
                ? `Repeat Cards: ${dataContentConfig.repeatLayout.columns} per row`
                : 'Single Content'}
            </small>
          ) : null}
        </div>
        <label>
          Zoom
          <input
            aria-label="Canvas zoom"
            max="2"
            min="0.35"
            onChange={(event) => {
              setZoom(Number(event.target.value));
            }}
            step="0.05"
            type="range"
            value={zoom}
          />
          {Math.round(zoom * 100)}%
        </label>
      </div>
      <div className="pdf-template-designer__canvas-scroll">
        <div
          className="pdf-template-designer__canvas-scale"
          style={{ height: template.canvas.height * zoom, width: template.canvas.width * zoom }}
        >
          <div
            className="pdf-template-designer__canvas"
            onPointerDown={handleCanvasPointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
            ref={canvasRef}
            style={{
              height: template.canvas.height,
              transform: `scale(${zoom})`,
              width: template.canvas.width,
            }}
          >
            {template.regions.header.enabled !== false ? (
              <div
                className={`pdf-template-designer__canvas-region is-header ${activeRegion === 'header' ? 'is-active' : ''}`}
                onPointerDown={(event) => { handleRegionPointerDown(event, 'header'); }}
                style={{
                  height: getRegionHeight(template, 'header'),
                  left: getCanvasMargins(template).left,
                  right: getCanvasMargins(template).right,
                  top: getCanvasMargins(template).top,
                }}
              >
                <span>Header</span>
              </div>
            ) : null}
            <div
              className={`pdf-template-designer__canvas-region is-data-content ${activeRegion === 'dataContent' ? 'is-active' : ''}`}
              onPointerDown={(event) => { handleRegionPointerDown(event, 'dataContent'); }}
              style={{
                height: getDataContentBounds(template).height,
                left: getCanvasMargins(template).left,
                right: getCanvasMargins(template).right,
                top: getDataContentBounds(template).y,
              }}
            >
              <span>Data Content</span>
            </div>
            {template.regions.footer.enabled !== false ? (
              <div
                className={`pdf-template-designer__canvas-region is-footer ${activeRegion === 'footer' ? 'is-active' : ''}`}
                onPointerDown={(event) => { handleRegionPointerDown(event, 'footer'); }}
                style={{
                  height: getRegionHeight(template, 'footer'),
                  left: getCanvasMargins(template).left,
                  right: getCanvasMargins(template).right,
                  top: template.canvas.height - getCanvasMargins(template).bottom - getRegionHeight(template, 'footer'),
                }}
              >
                <span>Footer</span>
              </div>
            ) : null}
            {regionElements[activeRegion].length === 0 ? (
              <div
                className="pdf-template-designer__canvas-empty-state"
                style={{
                  height: getRegionBounds(activeRegion).bottom - getRegionBounds(activeRegion).top,
                  left: getCanvasMargins(template).left,
                  pointerEvents: 'none',
                  top: getRegionBounds(activeRegion).top + (activeRegion === 'footer' ? -getCanvasMargins(template).bottom : getCanvasMargins(template).top),
                  width: template.canvas.width - getCanvasMargins(template).left - getCanvasMargins(template).right,
                }}
              >
                Add a text block or shape to start designing.
              </div>
            ) : null}
            {template.regions.header.enabled !== false ? regionElements.header.map((element) => {
              return renderElement(element, 'header');
            }) : null}
            {renderDataContent()}
            {template.regions.footer.enabled !== false ? regionElements.footer.map((element) => {
              return renderElement(element, 'footer');
            }) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplateCanvas;
